/**
 * Mascaras de digitacao, declaradas por atributo na view:
 *
 *   <input data-mascara="documento" />   000.000.000-00  ou  00.000.000/0000-00
 *   <input data-mascara="telefone" />    (00) 0000-0000  ou  (00) 00000-0000
 *   <input data-mascara="valor" />       10.000,50
 *   <input data-mascara="cep" />         00000-000
 *
 * Documento e telefone nao precisam ser desfeitos no envio: o servidor ja grava
 * so os digitos, via DocumentoHelper.SomenteDigitos.
 *
 * Valor precisa. A aplicacao roda em InvariantCulture (ver Program.cs), entao o
 * binder espera "10000.5" e recusaria "10.000,50" — e o jquery-validate
 * recusaria antes disso, pelo data-val-number. Por isso o campo volta ao
 * formato invariante num handler de submit na **fase de captura**, que roda
 * antes do jquery-validate, e a mascara e reposta depois se o envio for
 * barrado. Mesmo motivo pelo qual confirmacao.ts usa captura.
 */

type Formatador = (bruto: string) => string;

function somenteDigitos(valor: string): string {
    return valor.replace(/\D/g, '');
}

function agruparMilhares(inteiro: string): string {
    return inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function formatarDocumento(bruto: string): string {
    const digitos = somenteDigitos(bruto).slice(0, 14);

    // Ate 11 digitos e CPF; a partir do 12o vira CNPJ e a mascara troca sozinha.
    if (digitos.length <= 11) {
        return digitos
            .replace(/^(\d{3})(\d)/, '$1.$2')
            .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
            .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
    }

    return digitos
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
}

function formatarCep(bruto: string): string {
    const digitos = somenteDigitos(bruto).slice(0, 8);

    return digitos.replace(/^(\d{5})(\d)/, '$1-$2');
}

function formatarTelefone(bruto: string): string {
    const digitos = somenteDigitos(bruto).slice(0, 11);

    if (digitos.length <= 10) {
        return digitos
            .replace(/^(\d{2})(\d)/, '($1) $2')
            .replace(/^\((\d{2})\) (\d{4})(\d)/, '($1) $2-$3');
    }

    return digitos
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/^\((\d{2})\) (\d{5})(\d)/, '($1) $2-$3');
}

/**
 * Separador de milhar e virgula opcional com ate duas casas. Nao e mascara de
 * centavos: quem digita 10000 quer dez mil, nao cem reais. Num campo chamado
 * "valor estimado" o centavo raramente importa, e obrigar a digitar da direita
 * para a esquerda seria pegadinha.
 */
function formatarValor(bruto: string, naCarga = false): string {
    const limpo = bruto.replace(/[^\d,.]/g, '');

    // O ponto so e decimal na carga da pagina, onde o valor vem do servidor em
    // formato invariante ("10000.00"). Durante a digitacao ele e separador de
    // milhar: quem digita "1.000" quer mil, e ler isso como um real e dez
    // centavos seria a maior armadilha possivel num campo de valor.
    const invariante = naCarga && /^\d+\.\d{1,2}$/.test(limpo);
    const normalizado = invariante ? limpo.replace('.', ',') : limpo.replace(/\./g, '');

    const partes = normalizado.split(',');
    const inteiro = partes[0].replace(/^0+(?=\d)/, '');
    const temVirgula = partes.length > 1;
    const decimal = temVirgula ? partes.slice(1).join('').slice(0, 2) : '';

    if (inteiro === '' && !temVirgula) {
        return '';
    }

    const agrupado = agruparMilhares(inteiro === '' ? '0' : inteiro);

    return temVirgula ? `${agrupado},${decimal}` : agrupado;
}

function paraInvariante(exibido: string): string {
    if (!exibido) {
        return '';
    }

    return exibido.replace(/\./g, '').replace(',', '.');
}

const FORMATADORES: Record<string, Formatador> = {
    documento: formatarDocumento,
    telefone: formatarTelefone,
    valor: formatarValor,
    cep: formatarCep,
};

/**
 * Reposiciona o cursor contando quantos caracteres significativos ficaram a sua
 * esquerda. Sem isso, editar o meio do campo joga o cursor para o fim a cada
 * tecla e o usuario digita de tras para frente.
 */
function reposicionarCursor(campo: HTMLInputElement, anterior: string, posicaoAnterior: number): void {
    const significativos = anterior.slice(0, posicaoAnterior).replace(/[^\d]/g, '').length;

    let contados = 0;
    let posicao = campo.value.length;

    for (let i = 0; i < campo.value.length; i++) {
        if (/\d/.test(campo.value[i])) {
            contados++;
        }

        if (contados >= significativos) {
            posicao = i + 1;
            break;
        }
    }

    if (significativos === 0) {
        posicao = campo.value.search(/\d/) === -1 ? campo.value.length : campo.value.search(/\d/);
    }

    campo.setSelectionRange(posicao, posicao);
}

export function iniciarMascaras(escopo: ParentNode = document): void {
    const campos = escopo.querySelectorAll<HTMLInputElement>('[data-mascara]');

    campos.forEach(campo => {
        const formatador = FORMATADORES[campo.dataset.mascara || ''];

        if (!formatador) {
            return;
        }

        if (campo.value) {
            campo.value = campo.dataset.mascara === 'valor'
                ? formatarValor(campo.value, true)
                : formatador(campo.value);
        }

        campo.addEventListener('input', () => {
            const anterior = campo.value;
            const posicao = campo.selectionStart ?? anterior.length;

            campo.value = formatador(anterior);

            if (campo.value !== anterior) {
                reposicionarCursor(campo, anterior, posicao);
            }
        });
    });

    // Um handler por formulario, e so onde existe campo de valor.
    const formularios = new Set<HTMLFormElement>();

    escopo.querySelectorAll<HTMLInputElement>('[data-mascara="valor"]').forEach(campo => {
        if (campo.form) {
            formularios.add(campo.form);
        }
    });

    formularios.forEach(formulario => {
        formulario.addEventListener('submit', evento => {
            const campos = formulario.querySelectorAll<HTMLInputElement>('[data-mascara="valor"]');

            campos.forEach(campo => {
                campo.value = paraInvariante(campo.value);
            });

            // Se a validacao barrar o envio, o usuario continua na tela — e nao
            // pode ficar olhando para "10000.5" no lugar do que ele digitou.
            window.setTimeout(() => {
                if (evento.defaultPrevented) {
                    campos.forEach(campo => {
                        campo.value = formatarValor(campo.value);
                    });
                }
            }, 0);
        }, { capture: true });
    });
}
